import { Suspense, useState, useEffect } from 'react'
import Grid from '@material-ui/core/Grid'
import Paper from '@material-ui/core/Paper'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import { DropzoneDialog } from 'material-ui-dropzone'
import {
  createTheme,
  responsiveFontSizes,
  ThemeProvider,
  makeStyles,
} from '@material-ui/core/styles'
import { useLocation, Outlet, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { ErrorBoundary } from 'react-error-boundary'

import NavBar from '../sections/Navbar/Navbar'
import GridNavbar from '../sections/Navbar/GridNavBar/GridNavbar'

import Spinner from '../ui/Spinner'
import errorImage from '../../assets/images/error.png'

import { setOfflineLogo } from '../../redux/slices/settings'

let theme = createTheme({
  palette: {
    primary: {
      main: '#364C63',
    },
    secondary: {
      main: '#F3B440',
    },
    quotes: {
      main: '#40F3B4',
    },
    projects: {
      main: '#6892C5',
    },
    clients: {
      main: '#FACC15',
    },
    chedarbackground: {
      main: '#F9FAFB',
    },
    black: {
      main: '#000000',
    },
  },
})
theme = responsiveFontSizes(theme)

const useStyles = makeStyles(theme => ({
  root: {
    height: '100vh',
    backgroundColor: '#F9FAFB',
    overflowY: 'scroll',
  },
  breadCrumbContainer: {
    padding: theme.spacing(0.5),
  },
  mainContent: {
    padding: theme.spacing(1),
  },
  breadCrumbPaper: {
    padding: theme.spacing(0.5),
    // color: theme.palette.text.secondary,
    backgroundColor: '#F9FAFB',
    display: 'flex',
    justifyContent: 'center',
  },
  mainContentPaper: {
    padding: theme.spacing(1),
    color: theme.palette.text.secondary,
    flexGrow: 1,
    overflowY: 'auto',
    backgroundColor: '#F9FAFB',
  },
  mainContentDiv: {
    padding: theme.spacing(0.5),
    flexGrow: 1,
    backgroundColor: '#F9FAFB',
  },
  breadCrumb: {
    padding: theme.spacing(0.5),
  },
  rootError: {
    height: '100vh',
    backgroundColor: '#ECECEC',
  },
  flexLayout: {
    fontFamily: 'Open Sans',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  errorImg: {
    height: '60vh',
  },
  errorHeading: {
    fontWeight: 900,
    color: '#454B69',
    textAlign: 'center',
    fontSize: '2rem',
    paddingTop: '1.5rem',
    marginBottom: '1rem',
  },
  errorText: {
    fontWeight: 500,
    color: '#454B69',
    textAlign: 'center',
    fontSize: '1.5rem',
    marginTop: '1rem',
  },
  btn: {
    fontFamily: 'Open Sans',
    fontWeight: 400,
    padding: '20px',
    backgroundColor: '#454B69',
    color: 'white',
    width: '320px',
    margin: '0 auto',
    textAlign: 'center',
    fontSize: '1.5rem',
    borderRadius: '5px',
    cursor: ' ',
    transition: 'all .2s linear',
    '&:hover': {
      backgroundColor: 'rgba(69, 75, 105, 0.9)',
      transition: 'all .2s linear',
    },
  },
  icon: {
    marginRight: '0.25rem',
  },
}))

function ErrorFallback({ error }) {
  const classes = useStyles()
  const navigate = useNavigate()
  console.log('React error: ', error)
  const handleNavigation = () => {
    // We navigate to the home page on button click and then reload the page to reset react
    navigate('/')
    window.location.reload()
  }
  return (
    <div id='chedar-app-root' className={classes.rootError}>
      <div role='alert' className={classes.flexLayout}>
        <img src={errorImage} alt='Error' className={classes.errorImg} />
        <h1 className={classes.errorHeading}>Whoops! Something went wrong!</h1>
        <p className={classes.errorText}>
          If the problem persists please reach out to us at support@chedar.fr
        </p>
        <button
          className={classes.btn}
          onClick={() => {
            handleNavigation()
          }}
        >
          Back to home
        </button>
      </div>
    </div>
  )
}

function MainLayout() {
  const classes = useStyles()
  let { pathname } = useLocation()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [isImageUploadOpen, setIsImageUploadOpen] = useState(false)
  const hasOfflineLogo = JSON.parse(window.localStorage.getItem('offlineLogo'))
  const [logoUrl, setLogoUrl] = useState(hasOfflineLogo || null)
  const offlineLogo = useSelector(state => state.settings.offlineLogo)
  const userLoginStatus = useSelector(state => state.auth.userLoginStatus)
  // Checks if any part of the word has the string /quote in it
  const regexQuote = new RegExp('^/quote.*$')
  const reader = new FileReader()

  useEffect(() => {
    window.localStorage.setItem('offlineLogo', JSON.stringify(logoUrl))
  }, [logoUrl])
  useEffect(() => {
    // let logo = JSON.parse(window.localStorage.getItem('offlineLogo'))
    if (logoUrl) {
      dispatch(setOfflineLogo(logoUrl))
    } else {
      handleImageUploadOpen()
    }
  }, [dispatch, logoUrl])

  const handleImageUploadOpen = () => {
    setIsImageUploadOpen(true)
  }
  const handleImageUploadClose = () => {
    setIsImageUploadOpen(false)
  }

  const handleImageUploadSubmit = async files => {
    if (files) {
      reader.onload = event => {
        setLogoUrl(event.target.result)
        dispatch(setOfflineLogo(event.target.result))
      }
      reader.readAsDataURL(files[0])

      handleImageUploadClose()
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <div id='chedar-app-root' className={classes.root}>
        <Grid container spacing={0}>
          <Grid item xs={12}>
            {/* //* Changes navbar according to which page is being shown */}
            {regexQuote.test(pathname) ? <GridNavbar /> : <NavBar />}
          </Grid>
          {userLoginStatus && !regexQuote.test(pathname) && (
            <Grid container item xs={12} spacing={0} className={classes.breadCrumbContainer}>
              <Grid item xs={12}>
                <Paper className={classes.breadCrumbPaper}>
                  <ErrorOutlineIcon fontSize='small' className={classes.icon} />
                  {t('offlineQuotes:offlineMsg')}
                </Paper>
              </Grid>
            </Grid>
          )}

          <Grid container item xs={12} spacing={1} className={classes.mainContent}>
            <Grid item xs={12}>
              <Suspense fallback={<Spinner />}>
                <Outlet />
              </Suspense>
            </Grid>
          </Grid>
        </Grid>
        {userLoginStatus && (
          <DropzoneDialog
            open={isImageUploadOpen}
            onSave={handleImageUploadSubmit}
            acceptedFiles={['image/jpeg', 'image/png']}
            maxFileSize={5000000}
            filesLimit={1}
            onClose={handleImageUploadClose}
            showPreviewsInDropzone={true}
            showPreviews={false}
            dialogTitle={t('offlineQuotes:uploadLogoText')}
          />
        )}
      </div>
    </ThemeProvider>
  )
}

const MainLayoutErrorBound = () => {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <MainLayout />
    </ErrorBoundary>
  )
}

export default MainLayoutErrorBound
